import React, { useEffect, useState } from "react";
import magic from "./magic";
import { FaLightbulb, FaFire, FaTint, FaBatteryHalf } from "react-icons/fa";
import { setLightTimer } from "./controllers/lightsParameters";
import { writeHeaterParameter } from "./controllers/heaterParameters";
import "./HomePanel.css";

function HomePanel({ visible = true, bleStatus, onConnect, onConnectInternet, onForgetInternet, onDisconnect }) {
  const [transport, setTransport] = useState("ble");
  const [internetAccess, setInternetAccess] = useState(magic.hasInternetAccess());
  const [password, setPassword] = useState("");
  const [lightsOn, setLightsOn] = useState(false);
  const [immersionRemaining, setImmersionRemaining] = useState(0);
  const [lightsTimerSec, setLightsTimerSec] = useState(0);
  const [hotWaterTemp, setHotWaterTemp] = useState(null);
  const [soc, setSoc] = useState(null);
  const [heaterRunning, setHeaterRunning] = useState(false);
  const [heaterTemp, setHeaterTemp] = useState(null);
  const [quality, setQuality] = useState({ transport: "ble", rtt: null, lastTelemetry: null, signalDbm: null });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const onLights = (lights) => {
      const on = (typeof lights.timer === "number" && lights.timer > 0) || (typeof lights.button === "string" && lights.button.toLowerCase() === "on");
      setLightsOn(!!on);
      if (typeof lights.timer === "number") setLightsTimerSec(lights.timer);
    };
    const onHotWater = (data) => {
      if (typeof data.immersion_timer_remaining === "number") setImmersionRemaining(data.immersion_timer_remaining);
      if (typeof data.temp === "number") setHotWaterTemp(data.temp);
    };
    const onBattery = (battery) => {
      if (typeof battery.soc === "number") setSoc(battery.soc);
    };
    const onHeater = (heater) => {
      if (typeof heater.running_state === "number") setHeaterRunning(heater.running_state !== 0);
      setHeaterTemp(Number.isFinite(heater.temp) ? heater.temp : null);
    };
    magic.bleEventDispatcher.on("lights", onLights);
    magic.bleEventDispatcher.on("hotWater", onHotWater);
    magic.bleEventDispatcher.on("battery", onBattery);
    magic.bleEventDispatcher.on("heater", onHeater);
    return () => {
      magic.bleEventDispatcher.removeListener("lights", onLights);
      magic.bleEventDispatcher.removeListener("hotWater", onHotWater);
      magic.bleEventDispatcher.removeListener("battery", onBattery);
      magic.bleEventDispatcher.removeListener("heater", onHeater);
    };
  }, [bleStatus]);

  useEffect(() => {
    const onToken = saved => {
      setInternetAccess(saved);
      if (saved) setPassword("");
    };
    magic.bleEventDispatcher.on("internet-token", onToken);
    return () => magic.bleEventDispatcher.removeListener("internet-token", onToken);
  }, []);

  useEffect(() => {
    const onQuality = next => setQuality(next);
    const timer = setInterval(() => setNow(Date.now()), 1000);
    magic.bleEventDispatcher.on("connection-quality", onQuality);
    return () => {
      clearInterval(timer);
      magic.bleEventDispatcher.removeListener("connection-quality", onQuality);
    };
  }, []);

  const toggleLights = async () => {
    if (bleStatus !== "connected") return;
    try {
      if (lightsOn) {
        await setLightTimer(0);
      } else {
        await setLightTimer(3600000);
      }
    } catch (e) {
      console.error("Quick action lights failed", e);
    }
  };

  const heaterOn = async () => {
    if (bleStatus !== "connected") return;
    if (!window.confirm("Turn on diesel heater for 24 hours?")) return;
    try {
      const timerMs = 24 * 60 * 60 * 1000;
      await writeHeaterParameter("timer",timerMs);
    } catch (e) {
      console.error("Quick action heater failed", e);
    }
  };

  const connected = bleStatus === "connected";
  const connect = () => transport === "ble"
    ? onConnect()
    : onConnectInternet(password);

  const getBatteryColor = () => {
    if (!connected || soc == null) return "#666";
    if (soc >= 80) return "#3a6";
    if (soc >= 40) return "#fa0";
    return "#c44";
  };

  const getLightColor = () => {
    if (!connected) return "#666";
    if (!lightsOn) return "#666";
    if (lightsTimerSec > 1800) return "#3a6";
    if (lightsTimerSec > 300) return "#fa0";
    return "#e67e22";
  };

  if (!visible) return null;

  return (
    <div className="home-panel">
      {/* Logo and Title */}
      <div className="home-header">
        <img src="/sprinter-mountains.png" className="home-logo" alt="logo" />
        <h1 className="home-title">Super Sprinter</h1>
      </div>

      {/* Connection Status */}
      {!connected ? (
        <div>
          <fieldset className="home-connection-options">
            <legend>Connection</legend>
            <label><input type="radio" name="vehicle-transport" value="ble" checked={transport === "ble"} onChange={() => setTransport("ble")} /> Bluetooth</label>
            <label><input type="radio" name="vehicle-transport" value="internet" checked={transport === "internet"} onChange={() => setTransport("internet")} /> Internet</label>
            {transport === "internet" && !internetAccess && (
              <div className="home-remote-fields">
                <label>Password<input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" /></label>
              </div>
            )}
            {transport === "internet" && internetAccess && (
              <button type="button" className="home-forget-access" onClick={onForgetInternet}>Forget saved access</button>
            )}
          </fieldset>
          <button className="home-connect-btn" onClick={connect}
            disabled={transport === "internet" && !internetAccess && !password}>
            {transport === "ble" ? "Connect over Bluetooth" : "Connect over Internet"}
          </button>
          {bleStatus !== 'disconnected' && <p role="status">{bleStatus}</p>}
        </div>
      ) : (
        <>
          {/* Status Bar */}
          <div className="home-status-bar">
            <span className="home-status-dot connected"></span>
            <span className="home-status-text">Connected</span>
            <button className="home-disconnect-btn" onClick={onDisconnect}>
              Disconnect
            </button>
          </div>
          <div className="home-connection-metrics" aria-label="Connection quality">
            <span>{quality.transport === "internet" ? "Internet" : "Bluetooth"}: {quality.rtt === null ? "measuring" : `${Math.round(quality.rtt / 10) * 10} ms`}</span>
            <span>Telemetry: {quality.lastTelemetry === null ? "waiting" : `${Math.max(0, (now - quality.lastTelemetry) / 1000).toFixed(1)} s ago`}</span>
            {quality.transport === "internet" && <span>4G signal at connect: {quality.signalDbm === null ? "waiting" : `${quality.signalDbm} dBm`}</span>}
          </div>

          {/* Quick Actions Grid */}
          <div className="home-actions-grid">
            <button
              className={`home-action-tile ${lightsOn ? 'active' : ''}`}
              onClick={toggleLights}
            >
              <FaLightbulb size={32} color={getLightColor()} />
              <span className="home-action-label">Lights</span>
              <span className="home-action-status">{lightsOn ? 'On' : 'Off'}</span>
            </button>

            <button
              className={`home-action-tile ${heaterRunning ? 'active' : ''}`}
              onClick={heaterOn}
            >
              <FaFire size={32} color={heaterRunning ? '#fa0' : '#666'} />
              <span className="home-action-label">Heater</span>
              <span className="home-action-status">
                {heaterTemp != null ? `${Math.round(heaterTemp)}°C` : (heaterRunning ? 'On' : 'Off')}
              </span>
            </button>

            <div className="home-action-tile">
              <FaTint size={32} color={immersionRemaining > 0 ? '#3a6' : '#666'} />
              <span className="home-action-label">Hot Water</span>
              <span className="home-action-status">
                {hotWaterTemp != null ? `${Math.round(hotWaterTemp)}°C` : '—'}
              </span>
            </div>

            <div className="home-action-tile">
              <FaBatteryHalf size={32} color={getBatteryColor()} />
              <span className="home-action-label">Battery</span>
              <span className="home-action-status">
                {soc != null ? `${Math.round(soc)}%` : '—'}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HomePanel;
